"use client";

import Image from "next/image";
import { useRef, useState } from "react";

const SWIPE_THRESHOLD_PX = 40;

// Pass a `key` that changes with the variant (see BuyExperience) so a new
// image set always starts the gallery back at index 0 -- a full remount via
// key, not an effect that syncs state to a prop change.
export function ProductGallery({
  images, alt, onMainImageLoad,
}: { images: string[]; alt: string; onMainImageLoad?: () => void }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(images.length - 1, i + 1));

  const handlePointerDown = (event: React.PointerEvent) => {
    touchStart.current = { x: event.clientX, y: event.clientY };
  };

  // Decided on pointerup, not during move -- no preventDefault while dragging, so vertical page
  // scroll over the gallery is never blocked, only a clearly-horizontal gesture flips the image.
  const handlePointerUp = (event: React.PointerEvent) => {
    if (!touchStart.current) return;
    const dx = event.clientX - touchStart.current.x;
    const dy = event.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  return (
    <div className="gallery">
      <div
        className="gallery-main"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => (touchStart.current = null)}
      >
        <Image
          src={images[index]}
          alt={alt}
          fill
          sizes="(max-width: 900px) 100vw, 55vw"
          style={{ objectFit: "cover" }}
          priority
          // Signals BuyExperience that the shopper's own currently-visible image has actually finished
          // loading, so it's now safe to start background-preloading nearby colors without competing with
          // it for bandwidth/decode priority -- see ColorImagePreloader.
          onLoad={onMainImageLoad}
        />
        {/* Hidden entirely (not just disabled) below 2 images -- now the common case, since each color
            variant is a single photo (see variant-images.ts): a lone pair of permanently-disabled arrows
            sitting on a single-image gallery reads as broken chrome, not as "nothing to swipe to". Mirrors
            how the dots below already hide the same way for the same reason. */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="gallery-arrow prev"
              onClick={goPrev}
              disabled={index === 0}
              aria-label="Previous image"
            >
              ←
            </button>
            <button
              type="button"
              className="gallery-arrow next"
              onClick={goNext}
              disabled={index === images.length - 1}
              aria-label="Next image"
            >
              →
            </button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="gallery-dots">
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              className={`gallery-dot${i === index ? " active" : ""}`}
              onClick={() => setIndex(i)}
              aria-label={`Show image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
